import { useMemo, useState } from 'react';
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, OptionCard } from '@/components/ui';
import type { CoverageMatrixVM } from '../RoleCoverage/useCoverageMatrix';
import {
    buildClientRoleMapCsv,
    countClientCsvRows,
    downloadCsvText,
    type ClientCsvScope,
} from './roleMapCsv';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapExportPopover.module.css';

type ExportScope = 'currentApp' | 'allApps' | ClientCsvScope;

/**
 * EAA-1817 (design frame 55c): "Xuất CSV" opens a popover anchored under the
 * button — four scopes with their row counts, plus the "kèm cột mã vai trò"
 * option — instead of downloading immediately.
 *
 * WHY THIS EXISTS. The button used to fire the request on click, so there was no
 * screen for the action at all: no way to say WHICH rows, no row count before
 * committing, and the one implicit scope (the current selection, else everything)
 * was invisible. Frame 55c is the missing step.
 *
 * TWO SCOPES ARE SERVED BY THE SERVER AND TWO ARE NOT, and the split is a fact
 * about the data, not a shortcut — see `roleMapCsv.ts`. An uncovered pair has no
 * `RoleMap` row to export.
 *
 * The two client scopes need the coverage grid, which only the coverage views
 * load. On the list view they are offered but disabled with the reason stated,
 * rather than silently absent: "I cannot export what is not on screen" is
 * information, an option that vanishes between views is confusion.
 */
export function RoleMapExportPopover({
    listVm,
    matrixVm,
    coverageLoaded,
}: {
    listVm: RoleMapTableVM;
    matrixVm: CoverageMatrixVM;
    /** False on the list view — the coverage grid the client scopes read is idle there. */
    coverageLoaded: boolean;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState<ExportScope>('allApps');
    const [includeCodes, setIncludeCodes] = useState(false);

    const selectedAppCode = listVm.appFilter[0] ?? null;

    const counts = useMemo(
        () => ({
            uncovered: countClientCsvRows('uncovered', matrixVm.allRows, matrixVm.allColumns),
            needsAttention: countClientCsvRows(
                'needsAttention',
                matrixVm.allRows,
                matrixVm.allColumns
            ),
        }),
        [matrixVm.allRows, matrixVm.allColumns]
    );

    const options: {
        key: ExportScope;
        title: string;
        description: string;
        disabled?: boolean;
    }[] = [
        {
            key: 'currentApp',
            // No app filter set → no name to put in the parens, so use the bare
            // label rather than printing "Ứng dụng đang chọn ()".
            title: selectedAppCode
                ? t('console.roleMap.exportPopover.currentApp', { appName: selectedAppCode })
                : t('console.roleMap.exportPopover.currentAppBare'),
            description: selectedAppCode
                ? t('console.roleMap.exportPopover.currentAppMeta', { appCode: selectedAppCode })
                : t('console.roleMap.exportPopover.currentAppNone'),
            disabled: !selectedAppCode,
        },
        {
            key: 'allApps',
            title: t('console.roleMap.exportPopover.allApps'),
            description: t('console.roleMap.exportPopover.allAppsMeta', {
                count: listVm.totalLinks,
            }),
        },
        {
            key: 'uncovered',
            title: t('console.roleMap.exportPopover.uncovered'),
            description: coverageLoaded
                ? t('console.roleMap.exportPopover.rowCount', { count: counts.uncovered })
                : t('console.roleMap.exportPopover.needsCoverageView'),
            disabled: !coverageLoaded,
        },
        {
            key: 'needsAttention',
            title: t('console.roleMap.exportPopover.needsAttention'),
            description: coverageLoaded
                ? t('console.roleMap.exportPopover.rowCount', { count: counts.needsAttention })
                : t('console.roleMap.exportPopover.needsCoverageView'),
            disabled: !coverageLoaded,
        },
    ];

    const isServerScope = scope === 'currentApp' || scope === 'allApps';
    // The server export is org-scoped by contract — the default slice has no
    // endpoint at all, which is what `exportEnabled` already encodes.
    const blocked = isServerScope && !listVm.exportEnabled;

    function handleDownload() {
        if (isServerScope) {
            void listVm.exportCsv({
                appCode: scope === 'currentApp' ? selectedAppCode : null,
                includeCodes,
            });
            setOpen(false);
            return;
        }

        const text = buildClientRoleMapCsv({
            scope,
            rows: matrixVm.allRows,
            columns: matrixVm.allColumns,
            includeCodes,
            headers: {
                roleKey: t('console.roleMap.exportPopover.headers.roleKey'),
                roleName: t('console.roleMap.exportPopover.headers.roleName'),
                application: t('console.roleMap.exportPopover.headers.application'),
                appName: t('console.roleMap.exportPopover.headers.appName'),
                appRole: t('console.roleMap.exportPopover.headers.appRole'),
                appRoleKey: t('console.roleMap.exportPopover.headers.appRoleKey'),
                status: t('console.roleMap.exportPopover.headers.status'),
            },
            statusLabels: {
                uncovered: t('console.roleCoverage.roleAxisCard.stateUncovered'),
                paused: t('console.roleCoverage.roleAxisCard.statePaused'),
            },
        });
        downloadCsvText(t(`console.roleMap.exportPopover.filename.${scope}`), text);
        setOpen(false);
    }

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
            content={
                <div className={styles.exportPopover}>
                    <h3 className={styles.exportPopoverTitle}>
                        {t('console.roleMap.exportPopover.title')}
                    </h3>

                    <div
                        className={styles.exportPopoverOptions}
                        role="radiogroup"
                        aria-label={t('console.roleMap.exportPopover.title')}
                    >
                        {options.map((option) => (
                            <OptionCard
                                key={option.key}
                                inputType="radio"
                                name="role-map-export-scope"
                                value={option.key}
                                title={option.title}
                                description={option.description}
                                selected={scope === option.key}
                                disabled={option.disabled}
                                onSelect={() => setScope(option.key)}
                            />
                        ))}
                    </div>

                    <Checkbox
                        checked={includeCodes}
                        onChange={(event) => setIncludeCodes(event.target.checked)}
                    >
                        {t('console.roleMap.exportPopover.includeCodes')}
                    </Checkbox>

                    {blocked && (
                        <p className="portal-admin-hint">
                            {t('console.roleMap.page.exportTitleDisabled')}
                        </p>
                    )}

                    <div className={styles.exportPopoverActions}>
                        <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
                        <Button
                            type="primary"
                            disabled={blocked || listVm.exportBusy}
                            onClick={handleDownload}
                        >
                            {listVm.exportBusy
                                ? t('console.roleMap.page.exporting')
                                : t('console.roleMap.exportPopover.download')}
                        </Button>
                    </div>
                </div>
            }
        >
            <Button
                icon={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <path
                            d="M12 16V4m0 12 4-4m-4 4-4-4M4 20h16"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                }
            >
                {t('console.roleMap.page.exportCsv')}
            </Button>
        </Popover>
    );
}

export default RoleMapExportPopover;
